/**
 * CHECKLIST SCREEN — Simple Pickup / Delivery Inspection
 *
 * Supports: checkbox, photo, video, text, select inputs.
 * 5-6 items per checklist — simple JotForm style.
 * Listing photos open in a full-screen lightbox for comparison.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  TextInput, Alert, ActivityIndicator, Platform, Image,
  Modal, Dimensions, KeyboardAvoidingView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { colors, spacing, radii, fonts, RD } from '../../theme/colors';
import { api } from '../../services/api';
// STATIC, not a dynamic import: a lazy chunk would have to be fetched over the very
// connection that is already failing, which is the worst possible moment to need one.
import { uploadAll } from '../../services/mediaUpload';
import { getChecklistItems } from '../../data/mockData';
import ImageLightbox from '../../components/ImageLightbox';

const SCREEN = Dimensions.get('window');

interface ChecklistItem {
  id: string;
  label: string;
  type: 'checkbox' | 'photo' | 'video' | 'text' | 'select';
  required: boolean;
  options?: string[];
  helpText?: string;
}

export function ChecklistScreen({ route, navigation }: any) {
  const { jobId, type, job } = route.params; // type: 'pickup' | 'delivery', job: optional job data
  const [items, setItems]           = useState<ChecklistItem[]>([]);
  const [responses, setResponses]   = useState<Record<string, any>>({});
  const [mediaFiles, setMediaFiles] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  /** "Uploading 3 of 6" while files go up one at a time. A spinner with no number is
   *  indistinguishable from a hang, and that is what drivers have been staring at. */
  const [uploadNote, setUploadNote] = useState('');
  const [loading, setLoading]       = useState(true);
  const [lightboxUri, setLightboxUri] = useState<string | null>(null);
  const [restored, setRestored]     = useState(false);
  /** Local file -> uploaded url, so a second tap of Complete only sends what is missing. */
  const uploadedRef = useRef<Record<string, string>>({});
  const PERSIST_KEY = `cp_checklist_${jobId}_${type}`;

  // Convert a picked web URI to a self-contained data: URI. On the iOS PWA the OS image
  // picker can RELOAD the tab on return, which kills blob: URIs and wipes React state —
  // data: URIs survive a reload (and persist), and the upload path already handles them.
  const toDataUri = async (uri: string): Promise<string> => {
    if (Platform.OS !== 'web' || uri.startsWith('data:')) return uri;
    try {
      const blob = await (await fetch(uri)).blob();
      return await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result as string);
        r.onerror = () => reject(new Error('read failed'));
        r.readAsDataURL(blob);
      });
    } catch { return uri; }
  };

  // Downscale + re-encode a captured PHOTO before we hold/upload it, so field submits are small
  // and reliable on weak cell signal (a 5-10MB full-res iPhone photo -> ~0.3-0.6MB) — this is THE
  // fix for "Complete spins for minutes then nothing": most stalls were multi-MB photos timing out
  // on a bad signal. Web only (canvas); native relies on the ImagePicker quality setting. Returns a
  // data: URI (reload-safe + persistable) exactly like toDataUri, so the rest of the flow is unchanged.
  // Still clear enough to document condition. Falls back to the raw data URI if anything goes wrong.
  const prepPhotoForUpload = async (uri: string): Promise<string> => {
    if (Platform.OS !== 'web') return uri;
    let objUrl: string | null = null;
    try {
      const blob = await (await fetch(uri)).blob();
      if (!blob.type.startsWith('image/')) return await toDataUri(uri);
      objUrl = URL.createObjectURL(blob);
      // 🚨 `Image` HERE IS REACT NATIVE'S COMPONENT, NOT THE BROWSER'S CONSTRUCTOR - it is
      // in the `react-native` import at the top of this file, and it shadows the global.
      // `new Image()` on a forwardRef object throws, so this whole downscale has been
      // dead since it was written: every web photo went up at full camera resolution
      // through the catch below. Reach for the global explicitly.
      const img: HTMLImageElement = await new Promise((res, rej) => {
        const im = new (window as any).Image();
        im.onload = () => res(im);
        im.onerror = () => rej(new Error('img load failed'));
        im.src = objUrl as string;
      });
      const MAX = 1600;
      let w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
      if (!w || !h) return await toDataUri(uri);
      if (Math.max(w, h) > MAX) { const s = MAX / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return await toDataUri(uri);
      ctx.drawImage(img, 0, 0, w, h);
      return canvas.toDataURL('image/jpeg', 0.6);
    } catch {
      return await toDataUri(uri);
    } finally {
      if (objUrl) URL.revokeObjectURL(objUrl);
    }
  };

  // Restore in-progress checklist (checked items + captured photos) so a picker-triggered
  // reload can't wipe the driver's progress — THE fix for "checklist resets after 1 photo".
  useEffect(() => {
    AsyncStorage.getItem(PERSIST_KEY).then(raw => {
      if (raw) { try { const s = JSON.parse(raw); if (s.responses) setResponses(s.responses); if (s.mediaFiles) setMediaFiles(s.mediaFiles); } catch { /* ignore */ } }
      setRestored(true);
    }).catch(() => setRestored(true));
  }, [jobId, type]);

  // Snapshot progress so a picker-triggered reload can't wipe checked items + captured photos.
  // PERF (fixes "app very slow / not loading" on checklists): this used to run SYNCHRONOUSLY on
  // EVERY change (every keystroke in a note, every photo) and JSON.stringify ALL base64 media —
  // including multi-MB VIDEOS — then do a blocking localStorage write. That froze the main thread
  // on every interaction. Now: (1) DEBOUNCED (~700ms) so rapid changes don't each serialize; and
  // (2) we persist PHOTOS ONLY, never base64 VIDEOS (a single video blows the ~5MB quota + blocks
  // the thread stringifying it; it got dropped by the fallback anyway). A reload re-captures the
  // walkaround video; photos + checked items — the tedious part — are what must survive.
  useEffect(() => {
    if (!restored) return;
    const timer = setTimeout(() => {
      const QUOTA = 4_500_000;
      const photoMedia: Record<string, string[]> = {};
      for (const [k, arr] of Object.entries(mediaFiles)) {
        const keep = arr.filter((u) => u.startsWith('data:image'));
        if (keep.length) photoMedia[k] = keep;
      }
      let blob = JSON.stringify({ responses, mediaFiles: photoMedia });
      if (blob.length > QUOTA) blob = JSON.stringify({ responses }); // too many photos → at least keep checked items
      AsyncStorage.setItem(PERSIST_KEY, blob).catch(() => {});
    }, 700);
    return () => clearTimeout(timer);
  }, [restored, responses, mediaFiles]);

  // PICKUP shows CHECKS + PHOTOS + VIDEOS, but every item is something the driver
  // can do the MOMENT THEY ARRIVE, BEFORE uninstalling or disassembling anything.
  // The whole point is to complete the inspection pre-disassembly so the seller
  // can be paid first. The arrival-doable scoping lives in the checklist item
  // definitions themselves (backend checklist.routes.ts + mobile mockData
  // fallback): condition photos/videos as-is, on-arrival functional tests,
  // accessory inventory, seller questions, and visible-damage checks only.
  // Disassembly/loading/after-removal steps live in the lightbulb guide
  // (driverTips), not here. Delivery is unchanged (it may include install/after
  // steps).
  // Photos are mandatory PROOF on BOTH pickup and delivery — force every photo step to
  // required so it can never be skipped (+ delivery videos), for all products + any template
  // source. The backend enforces this too (rejects a submit missing required photos), so an
  // old/cached bundle can't bypass it; this is the up-to-date in-app gate.
  const enforceMedia = (its: ChecklistItem[]): ChecklistItem[] =>
    (its || []).map(i => {
      if (i.type === 'photo') return { ...i, required: true };
      if (i.type === 'video' && type === 'delivery') return { ...i, required: true };
      return i;
    });
  useEffect(() => {
    api.get(`/checklists/${jobId}/${type}`)
      .then(({ data }) => { setItems(enforceMedia(data.items)); setLoading(false); })
      .catch(() => {
        setItems(enforceMedia(getChecklistItems(type, job)));
        setLoading(false);
      });
  }, [jobId, type]);

  const setValue = (id: string, value: any) =>
    setResponses((prev) => ({ ...prev, [id]: value }));

  const pickMedia = async (itemId: string) => {
    const add = async (uri: string) => {
      const d = await prepPhotoForUpload(uri); // downscale + reload-safe + persistable
      setMediaFiles((prev) => ({ ...prev, [itemId]: [...(prev[itemId] ?? []), d] }));
    };
    // Library picker first — on mobile web/iOS it offers BOTH "Take Photo" and "Choose
    // from Library" and is more reliable than the camera-only path that left drivers
    // unable to add pictures. Falls back to the camera. quality 0.6 keeps uploads small.
    try {
      const lib = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 0.6 });
      if (!lib.canceled && lib.assets?.[0]) { await add(lib.assets[0].uri); return; }
      if (lib.canceled) return;
    } catch {}
    try {
      const cam = await ImagePicker.launchCameraAsync({ quality: 0.6 });
      if (!cam.canceled && cam.assets?.[0]) await add(cam.assets[0].uri);
    } catch {
      Alert.alert('Could not add photo', 'Please allow photo/camera access for this site in your browser settings, then try again.');
    }
  };

  const pickVideo = async (itemId: string) => {
    // Convert to a data: URI like photos so the video survives a picker-triggered reload /
    // blob: revocation (the same hazard that wiped photos). Falls back to the raw uri if
    // conversion fails so the upload still has something.
    const add = async (uri: string) => {
      const d = await toDataUri(uri);
      setMediaFiles((prev) => ({ ...prev, [itemId]: [...(prev[itemId] ?? []), d] }));
    };
    // Library picker first — on mobile web/iOS it offers BOTH "Record Video" and "Choose
    // from Library" and is more reliable than the camera-only path that left drivers unable
    // to add videos. Falls back to the camera.
    try {
      const lib = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Videos, quality: 0.5, videoMaxDuration: 90 });
      if (!lib.canceled && lib.assets?.[0]) { await add(lib.assets[0].uri); return; }
      if (lib.canceled) return;
    } catch {}
    try {
      const cam = await ImagePicker.launchCameraAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Videos, quality: 0.5, videoMaxDuration: 90 });
      if (!cam.canceled && cam.assets?.[0]) await add(cam.assets[0].uri);
    } catch {
      Alert.alert('Could not add video', 'Please allow camera/photo access for this site in your browser settings, then try again.');
    }
  };

  const removeMedia = (itemId: string, idx: number) => {
    setMediaFiles(prev => ({
      ...prev,
      [itemId]: (prev[itemId] ?? []).filter((_, i) => i !== idx),
    }));
  };

  const completedCount = items.filter(i => {
    if (i.type === 'photo' || i.type === 'video') return (mediaFiles[i.id]?.length ?? 0) > 0;
    return responses[i.id] !== undefined && responses[i.id] !== '' && responses[i.id] !== false;
  }).length;

  const isComplete = items
    .filter((i) => i.required)
    .every((i) => {
      if (i.type === 'photo' || i.type === 'video') return (mediaFiles[i.id]?.length ?? 0) > 0;
      return responses[i.id] !== undefined && responses[i.id] !== '' && responses[i.id] !== false;
    });

  const handleSubmit = async () => {
    if (!isComplete) { Alert.alert('Incomplete', 'Please fill all required fields.'); return; }
    setSubmitting(true);

    const responseData = JSON.stringify(
      items.map((i) => ({
        itemId: i.id,
        label: i.label || i.id,
        value: responses[i.id] ?? null,
        mediaCount: mediaFiles[i.id]?.length ?? 0,
      }))
    );

    // Collect media split by PHOTO vs VIDEO (by the checklist item's type) so each goes to
    // the right multipart field. Videos MUST NOT be sent as 'photos' with a .jpg name —
    // Cloudinary then mishandles/rejects the .mov, which is why videos "weren't working".
    const photoUris: string[] = [];
    const videoUris: string[] = [];
    for (const item of items) {
      for (const uri of mediaFiles[item.id] ?? []) {
        (item.type === 'video' ? videoUris : photoUris).push(uri);
      }
    }
    const totalMedia = photoUris.length + videoUris.length;

    let uploadSuccess = false;

    /**
     * 🚨 EACH FILE ON ITS OWN, STRAIGHT TO R2 - the fix for the complaint drivers keep
     * making. Posting six files as ONE multipart request with a 75s window is unwinnable
     * on a weak signal: it times out, the server receives nothing, and the driver is told
     * to add photos they already took. One job was retried seven times on 2026-09-04.
     *
     * A dropped connection now costs one photo and one retry instead of the whole
     * checklist, and whatever DID upload is kept. Falls back to the old multipart path
     * only if signing is unavailable (R2 off), so an environment without R2 still works.
     */
    if (totalMedia > 0) {
      let signingUnavailable = false;
      try {
        setUploadNote(`Uploading 0 of ${totalMedia}...`);
        const up = await uploadAll(photoUris, videoUris, (done, total) => {
          setUploadNote(`Uploading ${done} of ${total}...`);
        }, uploadedRef.current);
        Object.assign(uploadedRef.current, up.urlByUri);
        signingUnavailable = up.signingUnavailable;
        if (!up.signingUnavailable) {
          setUploadNote('');

          /**
           * 🚨 NAME THE PHOTO THAT DID NOT UPLOAD.
           *
           * 2026-10-13, Norman: four photos and a video on screen, Complete tapped six times,
           * and every time the same photo never arrived. We posted the other four, the server
           * refused (4 files for 5 required items) and he was told to "add the required
           * photos" with no hint which one. He gave up and the office told him to skip it.
           *
           * Each file is tied back to its item. A failed file is removed from that item so it
           * shows as not done, and the driver is told exactly which one to retake. A retake is
           * a fresh file, which is what fixes it whether the old one was cleared by the phone,
           * unreadable, or the wrong type. Anything that DID go up is kept and not sent again.
           */
          const failedUris = new Set(up.failures.map((f) => f.uri));
          const mediaByItem: Record<string, string[]> = {};
          const blocked: ChecklistItem[] = [];
          for (const item of items) {
            if (item.type !== 'photo' && item.type !== 'video') continue;
            const uris = mediaFiles[item.id] ?? [];
            const urls = uris.map((u) => uploadedRef.current[u]).filter(Boolean) as string[];
            mediaByItem[item.id] = urls;
            if (item.required && uris.length > 0 && urls.length === 0) blocked.push(item);
          }
          const uploadFailures = up.failures.map((f) => {
            const item = items.find((i) => (mediaFiles[i.id] ?? []).includes(f.uri));
            return { itemId: item?.id ?? null, label: item?.label ?? null, kind: f.kind, reason: f.reason };
          });
          if (failedUris.size > 0) {
            setMediaFiles((prev) => {
              const next: Record<string, string[]> = {};
              for (const [k, arr] of Object.entries(prev)) next[k] = arr.filter((u) => !failedUris.has(u));
              return next;
            });
          }

          const body = {
            responses: JSON.stringify(items.map((i) => ({
              itemId: i.id,
              label: i.label || i.id,
              value: responses[i.id] ?? null,
              mediaCount: (i.type === 'photo' || i.type === 'video') ? (mediaByItem[i.id]?.length ?? 0) : (mediaFiles[i.id]?.length ?? 0),
            }))),
            photoUrls: JSON.stringify(up.photoUrls),
            videoUrls: JSON.stringify(up.videoUrls),
            mediaByItem: JSON.stringify(mediaByItem),
            uploadFailures: JSON.stringify(uploadFailures),
          };

          // The files are already up, so a dropped connection here only needs this small
          // post again, not every photo re-sent in one big request.
          let lastErr: any = null;
          for (let attempt = 0; attempt < 3 && !uploadSuccess; attempt++) {
            try {
              await api.post(`/checklists/${jobId}/${type}`, body, { timeout: 30_000 });
              uploadSuccess = true;
            } catch (e: any) {
              if (e?.response?.status === 422) {
                setSubmitting(false);
                if (blocked.length > 0) {
                  const names = blocked.map((i) => `"${i.label || i.id}"`).join(', ');
                  Alert.alert(
                    blocked.length > 1 ? 'Retake these photos' : 'Retake this photo',
                    `The ${blocked.length > 1 ? 'shots' : 'shot'} for ${names} did not upload. ${blocked.length > 1 ? 'They are' : 'It is'} cleared on your screen now. Take ${blocked.length > 1 ? 'them' : 'it'} again and tap Complete. Everything else is saved.`,
                  );
                } else {
                  Alert.alert('Photos required', e?.response?.data?.message || 'Please add the required photos before completing this checklist.');
                }
                return;
              }
              lastErr = e;
              if (attempt < 2) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
            }
          }
          if (!uploadSuccess) {
            setSubmitting(false);
            console.warn('[Checklist] save failed after the files uploaded:', lastErr?.message || lastErr);
            Alert.alert('Could not submit', 'Your photos are uploaded and saved on this device. We could not reach the server to finish, so check your signal and tap Complete again.');
            return;
          }
        }
      } catch (e: any) {
        setUploadNote('');
        console.warn('[Checklist] per-file upload failed, falling back to multipart:', e?.message || e);
        signingUnavailable = true;
      }
      // Per-file upload could not run at all: let the old single post below try.
      if (!signingUnavailable && !uploadSuccess) { setSubmitting(false); return; }
    }

    // Fallback: the original single multipart post. Only reached when per-file upload
    // could not run at all (no R2 signing) or threw before submitting.
    if (totalMedia > 0 && !uploadSuccess) {
      try {
        // Build a FRESH FormData per attempt (a consumed FormData can't be reliably re-sent).
        const buildForm = async (): Promise<{ form: FormData; appended: number }> => {
          const formData = new FormData();
          formData.append('responses', responseData);
          let appended = 0;
          const appendMedia = async (uri: string, field: 'photos' | 'videos', i: number) => {
            const isVideo = field === 'videos';
            const ext = isVideo ? 'mov' : 'jpg';
            const mime = isVideo ? 'video/quicktime' : 'image/jpeg';
            const fileName = `checklist-${field}-${Date.now()}-${i}.${ext}`;
            // Per-file guard: one unreadable URI (e.g. a video blob: that died on a PWA
            // reload) must NOT abort the upload — skip it so the other photos still upload.
            try {
              if (Platform.OS === 'web' && (uri.startsWith('data:') || uri.startsWith('blob:'))) {
                const resp = await fetch(uri);
                const blob = await resp.blob(); // carries its real type (video/* or image/*)
                formData.append(field, blob, fileName);
              } else {
                formData.append(field, { uri, type: mime, name: fileName } as any);
              }
              appended++;
            } catch (e: any) {
              console.warn(`Checklist media skipped (unreadable ${field}):`, e?.message || e);
            }
          };
          for (let pi = 0; pi < photoUris.length; pi++) await appendMedia(photoUris[pi], 'photos', pi);
          for (let vi = 0; vi < videoUris.length; vi++) await appendMedia(videoUris[vi], 'videos', vi);
          return { form: formData, appended };
        };

        // Upload with RETRY + a fail-fast timeout — the fix for "spins for minutes then nothing".
        // Field signal is often weak, so ONE stalled attempt shouldn't strand the driver: we time
        // out at 75s and retry (fresh form) up to 3x with backoff. On WEB we must NOT set the
        // multipart Content-Type ourselves — the browser has to add the boundary, or the server
        // can't parse the body and the request hangs. A 422 (real "photos required") stops instantly.
        let lastErr: any = null;
        for (let attempt = 0; attempt < 3 && !uploadSuccess; attempt++) {
          const built = await buildForm();
          if (built.appended === 0) break;
          try {
            await api.post(`/checklists/${jobId}/${type}`, built.form, {
              headers: Platform.OS === 'web' ? undefined : { 'Content-Type': 'multipart/form-data' },
              timeout: 75000,
            });
            uploadSuccess = true;
          } catch (e: any) {
            if (e?.response?.status === 422) {
              setSubmitting(false);
              Alert.alert('Photos required', e?.response?.data?.message || 'Please add the required photos before completing this checklist.');
              return;
            }
            lastErr = e;
            if (attempt < 2) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
          }
        }
        if (!uploadSuccess && lastErr) console.warn('Checklist media upload failed after retries:', lastErr?.message || lastErr);
      } catch (uploadErr: any) {
        if (uploadErr?.response?.status === 422) {
          setSubmitting(false);
          Alert.alert('Photos required', uploadErr?.response?.data?.message || 'Please add the required photos before completing this checklist.');
          return;
        }
        console.warn('Checklist media upload failed:', uploadErr?.message || uploadErr);
      }
    }

    // If no photos or upload succeeded with no photos, submit JSON only
    let checklistSaved = uploadSuccess;
    if (!uploadSuccess) {
      // Reset mediaCount to 0 since photos didn't upload — prevents "Upload failed" on admin
      const fallbackData = JSON.stringify(
        items.map((i) => ({
          itemId: i.id,
          label: i.label || i.id,
          value: responses[i.id] ?? null,
          mediaCount: 0, // photos failed to upload, don't claim they exist
        }))
      );
      try {
        await api.post(`/checklists/${jobId}/${type}`, { responses: fallbackData });
        checklistSaved = true;
      } catch (e: any) {
        if (e?.response?.status === 422) {
          setSubmitting(false);
          Alert.alert('Photos required', e?.response?.data?.message || 'Please add the required photos before completing this checklist.');
          return;
        }
      }
    }

    // Nothing reached the server (driver offline / dead connection). Do NOT clear their
    // saved progress and do NOT navigate as "complete" — keep the snapshot and let them
    // retry, so we never falsely tell a driver they're done when the job didn't advance.
    if (!checklistSaved) {
      setSubmitting(false);
      Alert.alert(
        'Could not submit',
        'We couldn’t reach the server. Your progress is saved on this device — please check your connection and tap Submit again.'
      );
      return;
    }

    // 🚨 THE TRANSITION IS WHAT ACTUALLY ADVANCES THE JOB — it cannot be fire-and-forget.
    //
    // This used to swallow the error and fall straight through to "clear the snapshot and
    // navigate as complete". So on a flaky connection the driver was told the pickup was
    // done, their saved progress was DELETED so they could not retry, and the job sat at
    // the pickup stage on dispatch while they drove away. The guard above already refuses
    // to lie about the checklist save; the status transition needs the same honesty.
    //
    // But it must not over-correct into a hard block: `transitionJob` THROWS
    // "Invalid transition: X → X" when the job is already at the target, so a first call
    // that succeeded and lost its response would fail every retry and strand a driver on
    // site forever. So on failure we ask the server what the job's status actually is, and
    // only stop the driver when it genuinely has not advanced.
    const targetStatus = type === 'pickup' ? 'PICKUP_CHECKLIST_COMPLETE' : 'DELIVERY_CHECKLIST_COMPLETE';
    const path = type === 'pickup' ? 'complete-pickup' : 'complete-delivery';
    let advanced = false;
    for (let attempt = 0; attempt < 3 && !advanced; attempt++) {
      try {
        await api.post(`/jobs/${jobId}/${path}`);
        advanced = true;
      } catch (err: any) {
        console.warn(`Checklist completion transition failed (attempt ${attempt + 1}):`, err?.message);
        if (attempt < 2) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    if (!advanced) {
      // Did it actually land? A lost response looks identical to a real failure from here.
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        if (data?.status === targetStatus) advanced = true;
      } catch { /* still unreachable — treated as not advanced below */ }
    }
    if (!advanced) {
      setSubmitting(false);
      Alert.alert(
        'Could not finish',
        'Your checklist was saved, but we couldn’t mark this step complete. Your progress is kept on this device — please check your connection and tap Submit again.'
      );
      return; // keep the snapshot; do NOT navigate as complete
    }

    setSubmitting(false);
    AsyncStorage.removeItem(PERSIST_KEY).catch(() => {}); // progress submitted — clear the snapshot
    // Signal completion back to ActiveJobScreen so it can advance status
    navigation.navigate('ActiveJob', {
      completedAction: type === 'pickup' ? 'PICKUP_CHECKLIST_COMPLETE' : 'DELIVERY_CHECKLIST_COMPLETE',
      jobId,
    });
  };

  if (loading) return <View style={styles.loadingWrap}><ActivityIndicator size="large" color={RD.ink} /></View>;

  // Partition items by type so the layout can render the v5 numbered sections:
  // (1) Photos & video, (2) Condition notes, (3) Inspection checklist.
  const mediaItems = items.filter((i) => i.type === 'photo' || i.type === 'video');
  const noteItems  = items.filter((i) => i.type === 'text');
  const checkItems = items.filter((i) => i.type === 'checkbox' || i.type === 'select');

  // Sequentially number the visible sections (skip empty ones).
  const sectionNums: number[] = [];
  let n = 0;
  [mediaItems.length > 0, noteItems.length > 0, checkItems.length > 0].forEach((present) => {
    sectionNums.push(present ? ++n : 0);
  });
  const [mediaNum, noteNum, checkNum] = sectionNums;

  const SectionHeader = ({ num, label }: { num: number; label: string }) => (
    <View style={styles.sectionHead}>
      <View style={styles.sectionNum}><Text style={styles.sectionNumText}>{num}</Text></View>
      <Text style={styles.sectionLabel}>{label}</Text>
    </View>
  );

  const Thumbnails = ({ item }: { item: ChecklistItem }) => {
    const files = mediaFiles[item.id] ?? [];
    if (files.length === 0) return null;
    return (
      <View style={styles.thumbnailRow}>
        {files.map((uri, i) => (
          <View key={i} style={styles.thumbnailWrap}>
            {item.type === 'video' ? (
              <View style={styles.videoThumb}>
                <Ionicons name="videocam" size={24} color="#fff" />
                <Text style={styles.videoThumbLabel}>Video {i + 1}</Text>
              </View>
            ) : (
              <TouchableOpacity onPress={() => setLightboxUri(uri)} activeOpacity={0.85}>
                <Image source={{ uri }} style={styles.thumbnail} />
              </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.thumbnailRemove} onPress={() => removeMedia(item.id, i)}>
              <Ionicons name="close" size={12} color="#fff" />
            </TouchableOpacity>
          </View>
        ))}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => { if (lightboxUri) { setLightboxUri(null); } else { navigation.goBack(); } }} style={styles.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>{type === 'pickup' ? 'Pickup Inspection' : 'Delivery Checklist'}</Text>
          <Text style={styles.headerSub}>{completedCount} of {items.length} complete</Text>
        </View>
        <View style={{ width: 22 }} />
      </View>

      {/* Progress (RD.money) + Item N of M */}
      <View style={styles.progressBar}>
        <View style={[styles.progressFill, { width: `${items.length > 0 ? (completedCount / items.length) * 100 : 0}%` }]} />
      </View>

      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
        {/* Inspection pill + Item N of M */}
        <View style={styles.pillRow}>
          <View style={styles.pill}>
            <Ionicons name="clipboard-outline" size={13} color={RD.maroon} />
            <Text style={styles.pillText} numberOfLines={1}>
              {job?.productTitle ? `${job.productTitle} · inspection` : 'Inspection'}
            </Text>
          </View>
          {items.length > 0 && (
            <Text style={styles.itemCounter}>
              Item {Math.min(completedCount + 1, items.length)} of {items.length}
            </Text>
          )}
        </View>

        {/* Guidance card */}
        <View style={styles.guidanceCard}>
          <Ionicons name="information-circle-outline" size={18} color={RD.info} />
          <Text style={styles.guidanceText}>
            {type === 'pickup'
              ? 'Inspect the item with the seller BEFORE loading or disassembling anything. Take clear photos from every side (and a video if required), then submit to complete the inspection so the seller can be paid.'
              : 'Confirm the item with the buyer. Take photos, note any concerns, then confirm each point below.'}
          </Text>
        </View>

        {/* Order info card — lets driver verify item matches listing */}
        {job && (
          <View style={styles.orderInfoCard}>
            <View style={styles.orderInfoHeader}>
              <Ionicons name={type === 'pickup' ? 'search-outline' : 'checkmark-done-outline'} size={18} color={RD.ink} />
              <Text style={styles.orderInfoTitle}>
                {type === 'pickup' ? 'Listing Details — Compare to Physical Item' : 'Order Details — Verify Delivery'}
              </Text>
            </View>

            {/* Listing photos from WooCommerce — tap to open fullscreen */}
            {job.productImages && job.productImages.length > 0 && (
              <>
                <Text style={styles.listingPhotosLabel}>
                  {type === 'pickup' ? 'LISTING PHOTOS — tap to enlarge and compare' : 'ITEM PHOTOS'}
                </Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.listingPhotosRow}>
                  {job.productImages.map((uri: string, i: number) => (
                    <TouchableOpacity key={i} onPress={() => setLightboxUri(uri)} activeOpacity={0.85}>
                      <Image source={{ uri }} style={styles.listingPhoto} resizeMode="cover" />
                      <View style={styles.listingPhotoZoomHint}>
                        <Ionicons name="expand-outline" size={12} color="#fff" />
                      </View>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </>
            )}

            <View style={styles.orderInfoRow}>
              <Text style={styles.orderInfoLabel}>ORDER</Text>
              <Text style={styles.orderInfoValue}>#{job.wooOrderId || job.id}</Text>
            </View>
            {/* Show full product name from WooCommerce listing */}
            {job.productCategory && (
              <View style={styles.orderInfoRow}>
                <Text style={styles.orderInfoLabel}>LISTING</Text>
                <Text style={styles.orderInfoValue}>{job.productCategory}</Text>
              </View>
            )}
            <View style={styles.orderInfoRow}>
              <Text style={styles.orderInfoLabel}>ITEM</Text>
              <Text style={styles.orderInfoValue}>{job.productTitle || 'Unknown'}</Text>
            </View>
            {job.categorySlug && job.categorySlug !== 'general' && (
              <View style={styles.orderInfoRow}>
                <Text style={styles.orderInfoLabel}>CATEGORY</Text>
                <Text style={styles.orderInfoValue}>{job.categorySlug.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Text>
              </View>
            )}

            {/* Pickup: show seller info and pickup address */}
            {type === 'pickup' && (
              <>
                <View style={styles.orderInfoDivider} />
                <View style={styles.orderInfoRow}>
                  <Text style={styles.orderInfoLabel}>SELLER</Text>
                  <Text style={styles.orderInfoValue}>{job.sellerName || 'N/A'}</Text>
                </View>
                {job.sellerPhone && (
                  <View style={styles.orderInfoRow}>
                    <Text style={styles.orderInfoLabel}>PHONE</Text>
                    <Text style={styles.orderInfoValue}>{job.sellerPhone}</Text>
                  </View>
                )}
                <View style={styles.orderInfoRow}>
                  <Text style={styles.orderInfoLabel}>PICKUP</Text>
                  <Text style={styles.orderInfoValue}>{job.pickupAddress || 'N/A'}</Text>
                </View>
              </>
            )}

            {/* Delivery: show buyer info and delivery address */}
            {type === 'delivery' && (
              <>
                <View style={styles.orderInfoDivider} />
                <View style={styles.orderInfoRow}>
                  <Text style={styles.orderInfoLabel}>BUYER</Text>
                  <Text style={styles.orderInfoValue}>{job.buyerName || 'N/A'}</Text>
                </View>
                {job.buyerPhone && (
                  <View style={styles.orderInfoRow}>
                    <Text style={styles.orderInfoLabel}>PHONE</Text>
                    <Text style={styles.orderInfoValue}>{job.buyerPhone}</Text>
                  </View>
                )}
                <View style={styles.orderInfoRow}>
                  <Text style={styles.orderInfoLabel}>DELIVER TO</Text>
                  <Text style={styles.orderInfoValue}>{job.deliveryAddress || 'N/A'}</Text>
                </View>
              </>
            )}
          </View>
        )}

        {/* ── Section 1 — Photos & video ───────────────────────── */}
        {mediaItems.length > 0 && (
          <View style={styles.section}>
            <SectionHeader num={mediaNum} label="Photos & video" />
            <View style={styles.card}>
              <Text style={styles.cardHint}>Capture the full item from the front, plus any damage.</Text>
              {mediaItems.map((item) => {
                const count = mediaFiles[item.id]?.length ?? 0;
                return (
                  <View key={item.id} style={styles.mediaBlock}>
                    <View style={styles.mediaBlockHead}>
                      <Text style={styles.mediaBlockLabel}>{item.label}</Text>
                      {item.required && (
                        <View style={styles.requiredBadge}>
                          <Text style={styles.requiredText}>Required</Text>
                        </View>
                      )}
                    </View>
                    {item.helpText && <Text style={styles.helpText}>{item.helpText}</Text>}
                    {item.type === 'photo' ? (
                      <TouchableOpacity style={styles.photoBtn} onPress={() => pickMedia(item.id)} activeOpacity={0.8}>
                        <Ionicons name="camera-outline" size={18} color={RD.textSec} />
                        <Text style={styles.photoBtnText}>Photo{count > 0 ? ` (${count})` : ''}</Text>
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity style={styles.videoBtn} onPress={() => pickVideo(item.id)} activeOpacity={0.85}>
                        <Ionicons name="videocam-outline" size={18} color="#fff" />
                        <Text style={styles.videoBtnText}>Video{count > 0 ? ` (${count})` : ''}</Text>
                      </TouchableOpacity>
                    )}
                    <Thumbnails item={item} />
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* ── Section 2 — Condition notes ──────────────────────── */}
        {noteItems.length > 0 && (
          <View style={styles.section}>
            <SectionHeader num={noteNum} label="Condition notes" />
            <View style={styles.card}>
              {noteItems.map((item) => (
                <View key={item.id} style={styles.noteBlock}>
                  {item.label ? <Text style={styles.noteLabel}>{item.label}</Text> : null}
                  {item.helpText && <Text style={styles.helpText}>{item.helpText}</Text>}
                  <TextInput
                    style={styles.textInput}
                    value={responses[item.id] ?? ''}
                    onChangeText={(v) => setValue(item.id, v)}
                    multiline
                    placeholder="Note any wear, damage, or things to flag..."
                    placeholderTextColor={RD.textFaint}
                  />
                </View>
              ))}
            </View>
          </View>
        )}

        {/* ── Section 3 — Inspection checklist ─────────────────── */}
        {checkItems.length > 0 && (
          <View style={styles.section}>
            <SectionHeader num={checkNum} label="Inspection checklist" />
            <View style={styles.checklistCard}>
              {checkItems.map((item, i) => {
                if (item.type === 'select' && item.options) {
                  return (
                    <View key={item.id} style={[styles.checkRow, i > 0 && styles.checkRowBorder]}>
                      <View style={styles.selectInner}>
                        <Text style={styles.checkLabel}>{item.label}</Text>
                        {item.helpText ? <Text style={styles.checkHint}>{item.helpText}</Text> : null}
                        <View style={styles.selectRow}>
                          {item.options.map((opt: string) => (
                            <TouchableOpacity
                              key={opt}
                              style={[styles.selectBtn, responses[item.id] === opt && styles.selectBtnActive]}
                              onPress={() => setValue(item.id, opt)}
                            >
                              <Text style={[styles.selectText, responses[item.id] === opt && styles.selectTextActive]}>{opt}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>
                    </View>
                  );
                }
                const on = !!responses[item.id];
                return (
                  <TouchableOpacity
                    key={item.id}
                    style={[styles.checkRow, i > 0 && styles.checkRowBorder]}
                    onPress={() => setValue(item.id, !responses[item.id])}
                    activeOpacity={0.7}
                  >
                    <View style={[styles.checkBox, on && styles.checkBoxOn]}>
                      {on && <Ionicons name="checkmark" size={15} color="#fff" />}
                    </View>
                    <View style={styles.checkTextWrap}>
                      <Text style={styles.checkLabel}>{item.label}</Text>
                      {item.helpText ? <Text style={styles.checkHint}>{item.helpText}</Text> : null}
                    </View>
                    {item.required && (
                      <View style={styles.requiredBadge}>
                        <Text style={styles.requiredText}>Required</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}

        {/* CTA */}
        <TouchableOpacity
          style={[styles.completeBtn, (!isComplete || submitting) && styles.btnDisabled]}
          onPress={handleSubmit}
          disabled={!isComplete || submitting}
          activeOpacity={0.85}
        >
          {submitting ? (
            /* A bare spinner is indistinguishable from a hang, which is exactly what
               drivers have been reporting. Say what it is doing and how far along. */
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <ActivityIndicator color="#fff" />
              {uploadNote ? <Text style={styles.completeText}>{uploadNote}</Text> : null}
            </View>
          ) : (
            <Text style={styles.completeText}>Complete {type === 'pickup' ? 'Inspection' : 'Checklist'}</Text>
          )}
        </TouchableOpacity>

      </ScrollView>
      </KeyboardAvoidingView>

      {/* Image Lightbox — rotateable, zoomable */}
      <ImageLightbox images={lightboxUri ? [lightboxUri] : []} index={lightboxUri ? 0 : null} onClose={() => setLightboxUri(null)} />
    </View>
  );
}

const styles = StyleSheet.create({
  container:         { flex: 1, backgroundColor: RD.bg },
  flex:              { flex: 1 },
  loadingWrap:       { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: RD.bg },

  // Header
  header:            { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.lg, paddingTop: Platform.OS === 'ios' ? 56 : 36, paddingBottom: spacing.md, backgroundColor: RD.surface, borderBottomWidth: 1, borderBottomColor: RD.border },
  backBtn:           { padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  headerCenter:      { flex: 1, alignItems: 'center' },
  headerTitle:       { fontSize: 16, fontWeight: '700', color: RD.text, fontFamily: fonts.semiBold },
  headerSub:         { fontSize: 12, color: RD.textFaint, marginTop: 2 },

  // Progress (RD.money)
  progressBar:       { height: 4, backgroundColor: RD.g07 },
  progressFill:      { height: 4, backgroundColor: RD.money, borderRadius: 2 },

  scroll:            { padding: spacing.base, gap: 16, paddingBottom: 40 },

  // Pill + counter
  pillRow:           { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  pill:              { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: RD.maroonSoft, borderRadius: radii.full, paddingHorizontal: 12, paddingVertical: 6, flexShrink: 1 },
  pillText:          { fontSize: 12, fontWeight: '700', color: RD.maroon, flexShrink: 1 },
  itemCounter:       { fontSize: 12, fontWeight: '700', color: RD.textFaint },

  // Guidance card
  guidanceCard:      { flexDirection: 'row', alignItems: 'flex-start', gap: 11, backgroundColor: RD.infoSoft, borderRadius: 18, padding: 14, borderWidth: 1, borderColor: RD.info + '25' },
  guidanceText:      { flex: 1, fontSize: 12.5, color: RD.textSec, lineHeight: 18 },

  // Order info card
  orderInfoCard:     { backgroundColor: RD.surface, borderRadius: 18, padding: spacing.base, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.03, shadowRadius: 8, elevation: 1 },
  orderInfoHeader:   { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  orderInfoTitle:    { fontSize: 14, fontWeight: '700', color: RD.text },
  listingPhotosLabel:{ fontSize: 10, fontWeight: '700', color: RD.textFaint, letterSpacing: 0.4, marginBottom: 6 },
  listingPhotosRow:  { marginBottom: 12, marginHorizontal: -4 },
  listingPhoto:      { width: 120, height: 120, borderRadius: radii.md, marginHorizontal: 4 },
  orderInfoDivider:  { height: 1, backgroundColor: RD.border, marginVertical: 8 },
  listingPhotoZoomHint: { position: 'absolute', bottom: 6, right: 10, backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 10, padding: 3 },
  orderInfoRow:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', paddingVertical: 5 },
  orderInfoLabel:    { fontSize: 10, fontWeight: '700', color: RD.textFaint, letterSpacing: 0.4, minWidth: 80 },
  orderInfoValue:    { flex: 1, fontSize: 13, color: RD.textSec, textAlign: 'right' },

  // Numbered sections
  section:           {},
  sectionHead:       { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 9 },
  sectionNum:        { width: 20, height: 20, borderRadius: 10, backgroundColor: RD.ink, justifyContent: 'center', alignItems: 'center' },
  sectionNumText:    { fontSize: 11, fontWeight: '700', color: '#fff' },
  sectionLabel:      { fontSize: 13, fontWeight: '700', color: RD.text },

  // Cards
  card:              { backgroundColor: RD.surface, borderRadius: 18, padding: spacing.base, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.03, shadowRadius: 8, elevation: 1 },
  cardHint:          { fontSize: 13, color: RD.textFaint, marginBottom: 12 },

  // Media block
  mediaBlock:        { marginBottom: 4 },
  mediaBlockHead:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 },
  mediaBlockLabel:   { flex: 1, fontSize: 14, fontWeight: '600', color: RD.text },
  photoBtn:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: RD.g05, borderRadius: radii.md, borderWidth: 1, borderColor: RD.borderStrong, height: 50 },
  photoBtnText:      { color: RD.textSec, fontWeight: '600', fontSize: 14 },
  videoBtn:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: RD.maroon, borderRadius: radii.md, height: 50 },
  videoBtnText:      { color: '#fff', fontWeight: '700', fontSize: 14 },

  // Thumbnails
  thumbnailRow:      { flexDirection: 'row', flexWrap: 'wrap', gap: 9, marginTop: 12 },
  thumbnailWrap:     { position: 'relative' },
  thumbnail:         { width: 66, height: 66, borderRadius: 14 },
  thumbnailRemove:   { position: 'absolute', top: -7, right: -7, width: 22, height: 22, borderRadius: 11, backgroundColor: RD.ink, justifyContent: 'center', alignItems: 'center' },
  videoThumb:        { width: 66, height: 66, borderRadius: 14, backgroundColor: RD.ink, justifyContent: 'center', alignItems: 'center' },
  videoThumbLabel:   { color: '#fff', fontSize: 10, fontWeight: '600', marginTop: 2 },

  // Note block
  noteBlock:         {},
  noteLabel:         { fontSize: 14, fontWeight: '600', color: RD.text, marginBottom: 4 },
  helpText:          { fontSize: 12, color: RD.textFaint, lineHeight: 18, marginBottom: spacing.sm },
  textInput:         { borderWidth: 1, borderColor: RD.borderStrong, borderRadius: radii.md, padding: 14, fontSize: 15, color: RD.text, minHeight: 80, textAlignVertical: 'top', backgroundColor: RD.g03 },

  // Checklist
  checklistCard:     { backgroundColor: RD.surface, borderRadius: 18, paddingHorizontal: spacing.base, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.03, shadowRadius: 8, elevation: 1 },
  checkRow:          { flexDirection: 'row', alignItems: 'center', gap: 13, paddingVertical: 13, minHeight: 48 },
  checkRowBorder:    { borderTopWidth: 1, borderTopColor: RD.g07 },
  checkBox:          { width: 26, height: 26, borderRadius: 8, backgroundColor: RD.surface, borderWidth: 1.5, borderColor: RD.g15, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  checkBoxOn:        { backgroundColor: RD.money, borderColor: RD.money },
  checkTextWrap:     { flex: 1 },
  checkLabel:        { fontSize: 14.5, fontWeight: '600', color: RD.text },
  checkHint:         { fontSize: 12, color: RD.textFaint, marginTop: 1 },
  selectInner:       { flex: 1, paddingVertical: 2 },

  // Required badge
  requiredBadge:     { alignSelf: 'flex-start', backgroundColor: RD.dangerSoft, borderRadius: radii.full, paddingHorizontal: 8, paddingVertical: 2 },
  requiredText:      { fontSize: 10, fontWeight: '700', color: RD.danger },

  // Select
  selectRow:         { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  selectBtn:         { paddingHorizontal: 14, paddingVertical: 10, borderRadius: radii.full, borderWidth: 1, borderColor: RD.borderStrong },
  selectBtnActive:   { borderColor: RD.money, backgroundColor: RD.moneySoft },
  selectText:        { color: RD.textSec, fontSize: 13, fontWeight: '500' },
  selectTextActive:  { color: RD.money, fontWeight: '700' },

  // CTA
  completeBtn:       { backgroundColor: RD.ink, borderRadius: 18, height: 66, justifyContent: 'center', alignItems: 'center', marginTop: 4 },
  completeText:      { color: '#fff', fontWeight: '700', fontSize: 18, fontFamily: fonts.semiBold },
  btnDisabled:       { opacity: 0.5 },

  // Lightbox
  lightboxOverlay:   { flex: 1, backgroundColor: 'rgba(0,0,0,0.9)', justifyContent: 'center', alignItems: 'center', padding: SCREEN.width * 0.1 },
  lightboxImage:     { width: SCREEN.width * 0.8, height: SCREEN.height * 0.7, borderRadius: radii.lg },
  lightboxCloseHint: { position: 'absolute', top: Platform.OS === 'ios' ? 60 : 40, flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8 },
  lightboxCloseText: { color: colors.white, fontSize: 13, fontWeight: '600' },
});
